import http, { e } from "./http";
import loginService from "./login.service";
import {
  decryptBytes,
  createPdfBlobUrl,
  analyzeEncryptedData,
  validatePdfData,
} from "../utils/crypto";

const catalogService = {
  getCompanies() {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.get(e("catalog/company"), null, headers).then((r) => {
      return r.data;
    });
  },
  createGroup(group) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.post(e("catalog/group"), group, headers).then((r) => {
      return r.data;
    });
  },
  updateGroup(group) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.put(e("catalog/group"), group, headers).then((r) => {
      return r.data;
    });
  },
  deleteGroup(idGroup) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.remove(e(`catalog/group/${idGroup}`), headers).then((r) => {
      return r.data;
    });
  },
  createBrand(brand) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.post(e("catalog/brand"), brand, headers).then((r) => {
      return r.data;
    });
  },
  updateBrand(brand) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.put(e("catalog/brand"), brand, headers).then((r) => {
      return r.data;
    });
  },
  deleteBrand(idBrand) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.remove(e(`catalog/brand/${idBrand}`), headers).then((r) => {
      return r.data;
    });
  },
  createSubBrand(subBrand) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.post(e("catalog/subbrand"), subBrand, headers).then((r) => {
      return r.data;
    });
  },
  updateSubBrand(subBrand) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.put(e("catalog/subbrand"), subBrand, headers).then((r) => {
      return r.data;
    });
  },
  deleteSubBrand(idSubBrand) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http
      .remove(e(`catalog/subbrand/${idSubBrand}`), headers)
      .then((r) => {
        return r.data;
      });
  },
  createToy(toy) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.post(e("catalog/toy"), toy, headers).then((r) => {
      return r.data;
    });
  },
  updateToy(toy) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.put(e("catalog/toy"), toy, headers).then((r) => {
      return r.data;
    });
  },
  deleteToy(idToy) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.remove(e(`catalog/toy/${idToy}`), headers).then((r) => {
      return r.data;
    });
  },
  updateExhibition(exhibition) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.put(e("catalog/exhibition"), exhibition, headers).then((r) => {
      return r.data;
    });
  },
  async renderBrochure(brochure) {
    const token = loginService.getRawToken();

    try {
      console.log("Fetching brochure:", brochure.idBrochure);

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 60000); // 60 second timeout

      try {
        // Fetch encrypted PDF data with timeout
        const response = await fetch(
          e(`file/brochure/${brochure.idBrochure}?access_token=${token}`),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId); // Clear timeout if request completes

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get encrypted data as ArrayBuffer
        const encryptedData = await response.arrayBuffer();
        console.log("Received encrypted data, size:", encryptedData.byteLength);

        // Analyze encrypted data structure for debugging
        const analysis = analyzeEncryptedData(encryptedData);
        console.log("Encrypted data analysis:", analysis);

        // Decrypt the data
        const decryptedData = decryptBytes(encryptedData);

        // Validate the decrypted PDF
        const isValidPdf = validatePdfData(decryptedData);
        console.log("PDF validation result:", isValidPdf);

        if (!isValidPdf) {
          console.warn("Decrypted data does not appear to be a valid PDF");
          // Continue anyway as validation might be too strict
        }

        // Create blob URL for the decrypted PDF
        console.log("Creating blob URL for decrypted PDF...");
        const blobUrl = createPdfBlobUrl(decryptedData);
        console.log("✅ PDF blob URL ready for viewer:", blobUrl);

        return blobUrl;
      } catch (fetchError) {
        clearTimeout(timeoutId); // Clear timeout on error

        if (fetchError.name === "AbortError") {
          throw new Error(
            "Request timeout: PDF download took too long (30 seconds)"
          );
        }
        throw fetchError; // Re-throw other fetch errors
      }
    } catch (error) {
      console.error("Error rendering brochure:", error);
      console.error("Brochure details:", {
        idBrochure: brochure.idBrochure,
        hasToken: !!token,
      });
      throw error;
    }
  },
  getBrochures(pageFilter = null) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    // Default pagination parameters if not provided
    const defaultFilter = {
      page: 1,
      size: 10,
      searchText: "",
    };

    const filter = pageFilter || defaultFilter;

    return http.post(e("brochure/paged"), filter, headers).then((r) => {
      return r.data;
    });
  },
  createBrochure(brochure) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.post(e("brochure"), brochure, headers).then((r) => {
      return r.data;
    });
  },
  updateBrochure(brochure) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.put(e("brochure"), brochure, headers).then((r) => {
      return r.data;
    });
  },
  deleteBrochure(idBrochure) {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.remove(e(`brochure/${idBrochure}`), headers).then((r) => {
      return r.data;
    });
  },
  getBrochureStatuses() {
    const token = loginService.getRawToken();

    var headers = {
      Authorization: "Bearer " + token,
    };

    return http.get(e("brochure/status"), null, headers).then((r) => {
      return r.data;
    });
  },
};
export default catalogService;
